// hooks/auth/useAuth2FAVerify.ts
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectAuthError,
  selectFieldErrors,
  selectIsSubmitting,
  selectTwoFactorMethod,
  selectTwoFactorMaskedDestination,
  selectRegToken,
} from '@/store/selectors/auth';
import { verify2faCodeThunk } from '@/store/thunks/auth/verify2faCodeThunk';
import { resend2faCodeThunk } from '@/store/thunks/auth/resend2faCodeThunk';
import { clearLoginError } from '@/store/slices/auth/authSlice';
import { MIN_LEN, OTP_LENGTH } from '@/constants';
import type { TwoFactorMethod } from '@/types/AuthState';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { showSuccessToast } from '@/utils/toast.utils';

interface UseAuth2FAVerifyState {
  otp: string;
  isLoading: boolean;
  globalError: string | null;
  codeError: string | undefined;
  twoFactorMethod: TwoFactorMethod | undefined;
  maskedDestination: string | undefined;
}

interface UseAuth2FAVerifyHandlers {
  handleOtpChange: (newOtp: string) => void;
  handleSubmit: (event?: FormEvent) => void;
  handleResend: () => void;
  handleBack: () => void;
}

interface UseAuth2FAVerifyReturn {
  state: UseAuth2FAVerifyState;
  handlers: UseAuth2FAVerifyHandlers;
}

/**
 * Hook for /login/2fa/verify route
 * Handles 2FA code verification, OTP input state, code resend, and navigation
 *
 * Prerequisites:
 * - User must have sessionToken from credential verification
 * - User must have selected twoFactorMethod
 * - Route guard ensures these requirements
 */
export function useAuth2FAVerify(): UseAuth2FAVerifyReturn {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { handlers: loaderHandlers } = useGlobalLoader();

  const isLoading = useAppSelector(selectIsSubmitting);
  const error = useAppSelector(selectAuthError);
  const fieldErrors = useAppSelector(selectFieldErrors);
  const twoFactorMethod = useAppSelector(selectTwoFactorMethod);
  const maskedDestination = useAppSelector(selectTwoFactorMaskedDestination);
  const regToken = useAppSelector(selectRegToken);

  const [otp, setOtp] = useState('');
  const [isLocallySubmitting, setIsLocallySubmitting] = useState(false);

  useEffect(() => {
    dispatch(clearLoginError());
  }, [dispatch]);

  useEffect(() => {
    if (regToken === undefined || twoFactorMethod === undefined) {
      void navigate({ to: '/login' });
    }
  }, [regToken, twoFactorMethod, navigate]);

  const verify = useCallback(
    async(code: string) => {
      setIsLocallySubmitting(true);
      loaderHandlers.showLoader();
      try {
        const action = await dispatch(verify2faCodeThunk({ code }));
        if (verify2faCodeThunk.fulfilled.match(action)) {
          await navigate({ to: '/app' });
        }
      } finally {
        loaderHandlers.hideLoader();
        setIsLocallySubmitting(false);
      }
    },
    [dispatch, loaderHandlers, navigate],
  );

  const handleOtpChange = useCallback(
    (newOtp: string): void => {
      setOtp(newOtp);
    },
    [],
  );

  const handleSubmit = useCallback(
    (event?: FormEvent): void => {
      if (event !== undefined) event.preventDefault();
      if (otp.length === OTP_LENGTH) {
        void verify(otp);
      }
    },
    [otp, verify],
  );

  const handleResend = useCallback((): void => {
    if (twoFactorMethod === undefined) {
      void navigate({ to: '/login/2fa/select' });
      return;
    }
    loaderHandlers.showLoader();
    void dispatch(resend2faCodeThunk({ method: twoFactorMethod })).then((action) => {
      loaderHandlers.hideLoader();
      if (resend2faCodeThunk.fulfilled.match(action)) {
        showSuccessToast('Code resent');
      }
    });
  }, [dispatch, loaderHandlers, navigate, twoFactorMethod]);

  const handleBack = useCallback((): void => {
    void navigate({ to: '/login/2fa/select' });
  }, [navigate]);

  const globalError =
    typeof error === 'string' && error.length > MIN_LEN ? error : null;
  const codeError = fieldErrors.code;

  return {
    state: {
      otp,
      isLoading: isLoading || isLocallySubmitting,
      globalError,
      codeError,
      twoFactorMethod,
      maskedDestination,
    },
    handlers: {
      handleOtpChange,
      handleSubmit,
      handleResend,
      handleBack,
    },
  };
}
